import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { PAYMENT_CONFIRMED_EVENT } from '../../../api/api';
import { paymentRequests, PaymentStatus, PaymentStatusResponse } from '../../../api/payment/api';
import cacheQueryKeys from '../../../resources/queryCacheKeys';
import { useOnlineBookingReducer } from '../../../state/useOnlineBookingReducer';
import { Nullable } from '../../../types/types';
import { statusCodesMapper } from '../../../utils/exception';

import { useServerSideEvents } from './useServerSideEvents';

type ResultType = { status: PaymentStatus; inProgress: boolean; isError: boolean } | null;

export const usePaymentStatus = (invoiceToken?: string): Nullable<ResultType> => {
  const { dispatch, actions } = useOnlineBookingReducer();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { data } = useQuery<PaymentStatusResponse>([cacheQueryKeys.PaymentStatus]);
  const { mutate: sendServerEvent } = useServerSideEvents();

  const inProgress = data ? ['initiated', 'pending'].includes(data.status) : false;
  const hasError = data ? data.status === 'failed' : false;

  const fetchStatus = useCallback(async () => {
    setIsFetching(true);
    const response = await paymentRequests.fetchPaymentStatus(invoiceToken!);
    statusCodesMapper(response, {
      success: (paymentData) => {
        queryClient.setQueryData([cacheQueryKeys.PaymentStatus], response.data);
        switch (paymentData?.status) {
          case 'initiated':
          case 'pending':
            setTimeout(fetchStatus, 2000);
            break;
          case 'succeeded':
            sendServerEvent({
              eventName: PAYMENT_CONFIRMED_EVENT,
            });
            dispatch(actions.setSuccess());
            setIsFetching(false);
            break;
          case 'failed':
            setIsFetching(false);
            break;
          default:
            break;
        }
      },
      unhandled: () => {
        setIsFetching(false);
        queryClient.setQueryData([cacheQueryKeys.PaymentStatus], { status: 'failed' });
      },
    });
  }, [invoiceToken, queryClient, sendServerEvent, dispatch, actions]);

  useEffect(() => {
    if (data?.status && inProgress && invoiceToken && !isFetching) {
      fetchStatus();
    }
  }, [data?.status, invoiceToken, fetchStatus, queryClient, inProgress, isFetching]);

  return data ? { status: data.status, inProgress, isError: hasError } : null;
};
